import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReportRecordsTable from '../components/ReportRecordsTable';
import { Button } from '../components/adminModals';
import ReportActionModal from '../components/ReportActionModal';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    reportBatchService,
    ReportBatchItem,
    ApprovalReportData,
    APPROVAL_TYPE_LABELS,
} from '../services/adminAuth';
import { ArrowLeft, Info } from 'lucide-react';

function UpdateBatch() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const batch = (location.state as { batch?: ReportBatchItem } | null)?.batch ?? null;
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [generatedData, setGeneratedData] = useState<ApprovalReportData | null>(null);
    const [originalRefIds, setOriginalRefIds] = useState<Set<string>>(new Set());
    const [selectedRefIds, setSelectedRefIds] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    // 'draft' keeps isSubmitted:false, 'submit' sets isSubmitted:true.
    const [confirmMode, setConfirmMode] = useState<'draft' | 'submit' | null>(null);

    // The update payload needs the batch's period/type — bail out if we arrived without it.
    useEffect(() => {
        if (!batch || !id) {
            navigate('/admin/approval-requests', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const load = async () => {
            if (!batch || !id) return;
            setLoading(true);
            try {
                const [generated, items] = await Promise.all([
                    reportBatchService.generateReportForApproval({
                        fromDate: batch.startDate,
                        toDate: batch.endDate,
                        approvalType: batch.reportType,
                    }),
                    reportBatchService.getReportBatchItems(id),
                ]);

                if (generated.isSuccess) {
                    setGeneratedData(generated.data);
                } else {
                    showNotification('error', { title: 'Error', message: generated.message || 'Failed to load records' });
                }

                const current = new Set((items.isSuccess ? items.data.approvalRequestDetail ?? [] : []).map(d => d.referenceId));
                setOriginalRefIds(current);
                setSelectedRefIds(new Set(current));
            } catch (err) {
                showNotification('error', { title: 'Error', message: 'An error occurred while loading records' });
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const details = generatedData?.approvalRequestDetail ?? [];
    const allSelected = details.length > 0 && details.every(d => selectedRefIds.has(d.referenceId));

    const toggleRef = (referenceId: string) => {
        setSelectedRefIds(prev => {
            const next = new Set(prev);
            if (next.has(referenceId)) next.delete(referenceId);
            else next.add(referenceId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedRefIds(allSelected ? new Set() : new Set(details.map(d => d.referenceId)));
    };

    const addReferenceIds = Array.from(selectedRefIds).filter(id => !originalRefIds.has(id));
    const removeReferenceIds = Array.from(originalRefIds).filter(id => !selectedRefIds.has(id));
    const hasChanges = addReferenceIds.length > 0 || removeReferenceIds.length > 0;

    const openConfirm = (mode: 'draft' | 'submit') => {
        if (selectedRefIds.size === 0) {
            showNotification('warning', { title: 'Validation Error', message: 'Please keep at least one record in the batch.' });
            return;
        }
        // Submitting for processing is allowed even without record changes; saving a draft with no change is a no-op.
        if (mode === 'draft' && !hasChanges) {
            showNotification('warning', { title: 'No Changes', message: 'You have not changed the selected records.' });
            return;
        }
        setConfirmMode(mode);
    };

    const handleUpdate = async () => {
        if (!batch || !id || !confirmMode) return;
        const isSubmitted = confirmMode === 'submit';
        setSaving(true);
        try {
            const response = await reportBatchService.updateReportBatchRequestItems({
                reportBatchId: id,
                addReferenceIds,
                removeReferenceIds,
                type: batch.reportType,
                isSubmitted,
            });
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: isSubmitted ? 'Draft finalized — ready for submission' : 'Draft updated successfully',
                    timer: 3000,
                });
                setConfirmMode(null);
                navigate('/admin/approval-requests');
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to update the batch' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while updating the batch' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    <button
                        onClick={() => navigate('/admin/approval-requests')}
                        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Approval Requests
                    </button>

                    <div className="mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                            Edit Draft
                        </h1>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span>{batch?.title || APPROVAL_TYPE_LABELS[batch?.reportType ?? -1] || 'Report Batch'}</span>
                            {batch?.periodLabel && <span>{batch.periodLabel}</span>}
                        </div>
                    </div>

                    {/* Draft notice */}
                    <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
                        <Info size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-800">
                            This batch is a <span className="font-semibold">draft</span> and still editable. Add or remove records below,
                            then either keep it as a draft or finalize it as ready for submission. Once finalized it can no longer be
                            edited, and you can submit it for approval from the requests list.
                        </p>
                    </div>

                    <div className="bg-white rounded-[8px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] px-[16px] sm:px-[24px] py-[16px] sm:py-[20px]">
                        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#0A0A0A] m-0 mb-1">Records</h2>
                                <p className="text-xs sm:text-sm text-gray-500">
                                    <span className="font-semibold text-gray-700">{selectedRefIds.size}</span> of {details.length} selected
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                <Button variant="white" onClick={() => openConfirm('draft')} className="w-full sm:w-auto" disabled={saving || loading || selectedRefIds.size === 0}>
                                    Save as Draft
                                </Button>
                                <Button variant="success" onClick={() => openConfirm('submit')} className="w-full sm:w-auto" disabled={saving || loading || selectedRefIds.size === 0}>
                                    Mark Ready for Submission
                                </Button>
                            </div>
                        </div>

                        <ReportRecordsTable
                            data={generatedData}
                            loading={loading}
                            selectable
                            selectedRefIds={selectedRefIds}
                            onToggle={toggleRef}
                            onToggleAll={toggleSelectAll}
                            allSelected={allSelected}
                            reportType={batch?.reportType}
                            emptyMessage="No records found for this batch's period."
                        />
                    </div>

                    {/* Save Draft / Mark Ready confirmation */}
                    <ReportActionModal
                        isOpen={confirmMode !== null}
                        mode={confirmMode === 'submit' ? 'ready' : confirmMode === 'draft' ? 'draft' : null}
                        onClose={() => setConfirmMode(null)}
                        onConfirm={handleUpdate}
                        isSubmitting={saving}
                        title={batch?.title || APPROVAL_TYPE_LABELS[batch?.reportType ?? -1] || 'Report Batch'}
                        subtitle={batch?.periodLabel}
                        records={selectedRefIds.size}
                        changes={`${addReferenceIds.length} added, ${removeReferenceIds.length} removed`}
                    />
                </div>
            </div>
        </div>
    );
}

export default UpdateBatch;
